import {Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {HttpErrorResponse} from '@angular/common/http';
import {EMPTY, Observable, of} from 'rxjs';
import {catchError, filter, map, switchMap, tap} from 'rxjs/operators';
import {FolderService} from './folder.service';
import {NotificationService} from '../common/notification.service';
import {Folder, FolderItem} from '../../dtos/folder/folder';
import {FolderNameDialogComponent, FolderNameDialogData} from '../../components/folders/folder-name-dialog/folder-name-dialog.component';
import {ConfirmationDialogComponent} from '../../components/common/confirmation-dialog/confirmation-dialog.component';
import {FolderShareDialogComponent, FolderShareDialogData} from '../../components/folders/folder-share-dialog/folder-share-dialog.component';
import {FolderExportDialogComponent} from '../../components/folders/folder-export-dialog/folder-export-dialog.component';
import {AddToFolderDialogComponent, AddToFolderDialogData} from '../../components/folders/add-to-folder-dialog/add-to-folder-dialog.component';
import {folderErrorMessage} from '../../shared/folder/folder-utils';

interface FolderRef {
  id: number;
  name: string;
}

/**
 * the actions on a Sammelmappe that are available in the overview and the detail page: they open the
 * dialogs, send the request and show the result. The observables only emit on success, cancelling a
 * dialog or a failed request (already shown as snack bar) completes them without a value.
 */
@Injectable({
  providedIn: 'root'
})
export class FolderActionsService {

  constructor(private dialog: MatDialog,
              private folderService: FolderService,
              private notificationService: NotificationService) {
  }

  create(items?: FolderItem[]): Observable<Folder> {
    return this.askName({title: 'Neue Sammelmappe', action: 'Erstellen'}).pipe(
      switchMap(name => this.folderService.createFolder({name, items})),
      tap(folder => this.notificationService.openSnackBar('Sammelmappe „' + folder.name + '“ erstellt.')),
      catchError(error => this.handleError(error))
    );
  }

  rename(folder: FolderRef): Observable<Folder> {
    return this.askName({title: 'Sammelmappe umbenennen', action: 'Speichern', name: folder.name}).pipe(
      filter(name => name !== folder.name),
      switchMap(name => this.folderService.updateFolder(folder.id, {name})),
      tap(updated => this.notificationService.openSnackBar('Sammelmappe in „' + updated.name + '“ umbenannt.')),
      catchError(error => this.handleError(error))
    );
  }

  duplicate(folder: FolderRef): Observable<Folder> {
    return this.askName({title: 'Sammelmappe duplizieren', action: 'Duplizieren', name: folder.name + ' (Kopie)'}).pipe(
      switchMap(name => this.folderService.duplicateFolder(folder.id, name)),
      tap(copy => this.notificationService.openSnackBar('Sammelmappe „' + copy.name + '“ erstellt.')),
      catchError(error => this.handleError(error))
    );
  }

  delete(folder: FolderRef): Observable<void> {
    return this.confirm('Sammelmappe löschen',
      'Die Sammelmappe „' + folder.name + '“ wird unwiderruflich gelöscht, auch für alle Personen, mit denen sie '
      + 'geteilt ist. Die Einträge selbst bleiben erhalten.', 'Löschen').pipe(
      switchMap(() => this.folderService.deleteFolder(folder.id)),
      tap(() => this.notificationService.openSnackBar('Sammelmappe „' + folder.name + '“ gelöscht.')),
      catchError(error => this.handleError(error))
    );
  }

  /**
   * declines a pending share or removes an accepted one from the user's lists
   */
  leave(folder: FolderRef, pending: boolean): Observable<void> {
    const confirm$ = pending
      ? this.confirm('Sammelmappe ablehnen', 'Die Sammelmappe „' + folder.name + '“ wird abgelehnt. '
        + 'Die Person, die sie geteilt hat, wird benachrichtigt.', 'Ablehnen')
      : this.confirm('Sammelmappe entfernen', 'Die geteilte Sammelmappe „' + folder.name + '“ wird aus Ihren '
        + 'Sammelmappen entfernt. Sie haben danach keinen Zugriff mehr darauf.', 'Entfernen');
    return confirm$.pipe(
      switchMap(() => this.folderService.leave(folder.id)),
      tap(() => this.notificationService.openSnackBar(pending
        ? 'Sammelmappe „' + folder.name + '“ abgelehnt.'
        : 'Sammelmappe „' + folder.name + '“ entfernt.')),
      catchError(error => this.handleError(error))
    );
  }

  accept(folder: FolderRef): Observable<void> {
    return this.folderService.accept(folder.id).pipe(
      tap(() => this.notificationService.openSnackBar('Sammelmappe „' + folder.name + '“ angenommen.')),
      catchError(error => this.handleError(error))
    );
  }

  /**
   * opens the share dialog; emits when it is closed, as shares may have changed
   */
  share(folder: FolderRef): Observable<void> {
    const data: FolderShareDialogData = {folderId: folder.id, folderName: folder.name};
    return this.dialog.open(FolderShareDialogComponent, {data, autoFocus: 'first-tabbable'}).afterClosed().pipe(
      map(() => undefined)
    );
  }

  /**
   * opens the export dialog. It needs the entries of the list to offer only the sections that are
   * contained, so a folder without them (e.g. from the overview) is loaded first.
   */
  export(folder: Folder | FolderRef): void {
    const folder$: Observable<Folder> = 'vocabEntries' in folder
      ? of(folder as Folder)
      : this.folderService.getFolder(folder.id);
    folder$.subscribe({
      next: full => this.dialog.open(FolderExportDialogComponent, {data: {folder: full}}),
      error: error => this.handleError(error)
    });
  }

  /**
   * lets the user choose a list (or create one) and adds the given entries
   */
  addToFolder(items: FolderItem[], excludeFolderId?: number): Observable<boolean> {
    const data: AddToFolderDialogData = {items, excludeFolderId};
    return this.dialog.open(AddToFolderDialogComponent, {data}).afterClosed().pipe(
      filter(added => !!added)
    );
  }

  private askName(data: FolderNameDialogData): Observable<string> {
    return this.dialog.open(FolderNameDialogComponent, {data}).afterClosed().pipe(
      filter((name: string) => !!name)
    );
  }

  private confirm(headline: string, content: string, action: string): Observable<boolean> {
    return this.dialog.open(ConfirmationDialogComponent, {data: {headline, content, action}}).afterClosed().pipe(
      filter(confirmed => !!confirmed)
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    this.notificationService.openSnackBar(folderErrorMessage(error, {
      404: 'Die Sammelmappe existiert nicht mehr.'
    }));
    return EMPTY;
  }
}
